/**
 * TaskLetterZone (§ LETTERS-GROUP-A item 3, s126) — task-level ESTATE letters (CRA, credit
 * bureaus): one "Generate letter" button per declared estate template, NOT a per-record
 * RecordRow action. Reuses the per-record letter machinery: the same 422 gap dialog, the same
 * signed-URL download, and state derived from `letter_generated` events keyed by instanceKey
 * (`<type>::estate`). Hidden when the task declares no task-level letters.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Check, FileText, Loader2 } from "lucide-react";
import { LetterGapDialog, type LetterGapField } from "./LetterGapDialog";
import { deriveLetterStates, downloadLetter, type TaskEventLite } from "./binding-helpers";
import { SAGE } from "@shared/brand";

const SAGE_200 = "#C5D2C0";

export interface TaskLetter { templateType: string; name: string }

export function TaskLetterZone({ clientId, taskKey, taskLetters, events, eventsKey }: {
  clientId: string;
  taskKey: string;
  taskLetters: TaskLetter[];
  events: TaskEventLite[];
  eventsKey: unknown[];
}) {
  const qc = useQueryClient();
  const letterStates = deriveLetterStates(events);
  const [gap, setGap] = useState<{ templateType: string; fields: LetterGapField[] } | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  if (!taskLetters || taskLetters.length === 0) return null;

  const generate = async (templateType: string, profileFields?: Record<string, string>) => {
    setPending(templateType);
    try {
      const res = await fetch(`/api/executor/${clientId}/tasks/${taskKey}/letter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profileFields ? { templateType, profileFields } : { templateType }),
      });
      // 422 is the intentional gap-flow contract (mirrors the per-record route): collect the
      // missing executor-profile fields, then re-POST.
      if (res.status === 422) {
        const b = await res.json();
        setGap({ templateType, fields: (b.missingFields ?? []) as LetterGapField[] });
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as { letterId: string };
      setGap(null);
      await downloadLetter(clientId, data.letterId);
      qc.invalidateQueries({ queryKey: eventsKey });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="mt-4" data-testid="task-letter-zone">
      <div className="uppercase mb-2" style={{ color: SAGE[600], fontSize: 11, fontWeight: 600, letterSpacing: "0.06em" }}>
        Letters to send
      </div>
      <div className="flex flex-wrap gap-2">
        {taskLetters.map((tl) => {
          const state = letterStates.get(`${tl.templateType}::estate`);
          const busy = pending === tl.templateType;
          return state?.generated ? (
            <button key={tl.templateType} type="button"
              onClick={() => state.letterId && downloadLetter(clientId, state.letterId)}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[12px] font-medium"
              style={{ border: `1px solid ${SAGE_200}`, color: SAGE[600], backgroundColor: SAGE[50] }}
              data-testid={`task-letter-${tl.templateType}`}>
              <FileText className="h-3 w-3" /> {tl.name} <Check className="h-3 w-3 inline-block align-[-1px]" aria-hidden="true" /> · Download again
            </button>
          ) : (
            <button key={tl.templateType} type="button"
              onClick={() => generate(tl.templateType)} disabled={busy}
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-[12px] font-medium"
              style={{ border: `1px solid ${SAGE_200}`, color: SAGE[600] }}
              data-testid={`task-letter-${tl.templateType}`}>
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
              Generate {tl.name} letter
            </button>
          );
        })}
      </div>
      {gap && (
        <LetterGapDialog
          fields={gap.fields}
          onSubmit={(v) => generate(gap.templateType, v)}
          onCancel={() => setGap(null)}
          submitting={pending === gap.templateType}
        />
      )}
    </div>
  );
}
